import React, { useEffect, useRef, useState, useCallback } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";

const fallbackCenter = { lat: 48.8566, lng: 2.3522 };
const DEFAULT_ZOOM = 12;
const SINGLE_MARKER_ZOOM = 15;
const PADDING = { top: 100, right: 100, bottom: 100, left: 100 };

const MapWithResults = ({
  data = [],
  dataLatitudeKey = "lat",
  dataLongitudeKey = "lng",
  hoveredId = null,
  setHoveredId = () => { },
  isGoogleLoaded,
  single = false,
  onMarkerClick = () => { },
}) => {
  const mapRef = useRef(null);
  const [processedData, setProcessedData] = useState([]);
  const [mapCenter, setMapCenter] = useState(fallbackCenter);
  const [mapZoom, setMapZoom] = useState(DEFAULT_ZOOM);
  const [loading, setLoading] = useState(true);
  const hasCentered = useRef(false); // Nouvelle référence pour suivre si on a déjà centré

  // Geocode les adresses manquantes
  const processData = useCallback(async () => {
    if (!isGoogleLoaded || !window.google || !Array.isArray(data)) return;

    setLoading(true);

    try {
      // console.log(data)
      const results = await Promise.all(
        data.map(async (item) => {
          // console.log({item})
          if (item[dataLatitudeKey] && item[dataLongitudeKey]) {
            return item;
          }

          if (item.formatted_address) {
            // console.log('formatted_address',item.formatted_address)
            try {
              const geocoder = new window.google.maps.Geocoder();
              const response = await geocoder.geocode({ address: item.formatted_address });
              // console.log({response})
              if (response.results?.[0]?.geometry?.location) {
                return {
                  ...item,
                  [dataLatitudeKey]: response.results[0].geometry.location.lat(),
                  [dataLongitudeKey]: response.results[0].geometry.location.lng(),
                };
              }
            } catch (error) {
              // console.error("Geocoding error:", error);
            }
          }
          return null;
        })
      );

      setProcessedData(results.filter(Boolean));
    } finally {
      setLoading(false);
    }
  }, [data, isGoogleLoaded, dataLatitudeKey, dataLongitudeKey]);

  // Met à jour les bounds de la carte (uniquement au premier chargement)
  const updateMapBounds = useCallback(() => {
    if (!mapRef.current || !isGoogleLoaded || hasCentered.current) return;

    const validMarkers = processedData.filter(
      item => item[dataLatitudeKey] && item[dataLongitudeKey]
    );

    if (validMarkers.length === 0) {
      mapRef.current.setCenter(fallbackCenter);
      mapRef.current.setZoom(DEFAULT_ZOOM);
      hasCentered.current = true;
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    validMarkers.forEach(item => {
      bounds.extend({
        lat: Number(item[dataLatitudeKey]),
        lng: Number(item[dataLongitudeKey]),
      });
    });

    if (validMarkers.length === 1) {
      mapRef.current.setCenter(bounds.getCenter());
      mapRef.current.setZoom(SINGLE_MARKER_ZOOM);
    } else {
      mapRef.current.fitBounds(bounds, PADDING);
    }

    setMapCenter(bounds.getCenter());
    setMapZoom(validMarkers.length === 1 ? SINGLE_MARKER_ZOOM : DEFAULT_ZOOM);
    hasCentered.current = true; // Marquer comme centré
  }, [processedData, isGoogleLoaded]);

  // Effects
  useEffect(() => {
    processData();
  }, [isGoogleLoaded,processData]);
  useEffect(() => {
    hasCentered.current = false; // Reset before processing new data
    processData();
  }, [data]); // Trigger when `data` changes
  useEffect(() => {
    if (processedData.length > 0 && !hasCentered.current) {
      updateMapBounds();
    }


  },  [processedData, updateMapBounds]);

  const onLoad = useCallback((map) => {
    mapRef.current = map;
    updateMapBounds();
  }, [updateMapBounds]);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
    hasCentered.current = false; // Réinitialiser pour le prochain chargement
  }, []);

  if (!isGoogleLoaded) return <div className="map-loading">Chargement de la carte...</div>;
  if (loading) return <div className="map-loading">Traitement des lieux...</div>;

  const containerStyle = {
    width: "100%",
    height: single ? "400px" : "100vh",
    position: "sticky",
    top: "0",
    borderRadius: "8px",
    overflow: "hidden",
  };
  return (
    <div className="map-container" style={containerStyle}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={mapCenter}
        zoom={mapZoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={{
          scrollwheel: false, // Désactive le zoom avec la molette
          
          // disableDefaultUI: false, // Keep this false to show default controls
          zoomControl: true, // Explicitly enable zoom control
          zoomControlOptions: {
            position: window.google.maps.ControlPosition.RIGHT_TOP // Position the controls
          },
          // disableDefaultUI: true,
          gestureHandling: "greedy",
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }],
            },
            { gestureHandling: 'greedy' }
            // Désactive l'infobulle au survol
            // {
            //   featureType: "all",
            //   elementType: "labels",
            //   stylers: [{ visibility: "off" }]
            // }
          ],
        }}
      >
        {processedData.map((item) => {
          const lat = Number(item[dataLatitudeKey]);
          const lng = Number(item[dataLongitudeKey]);
          const id = item.id || item.google_place_id;
          // console.log({lat,lng})
          if (isNaN(lat) || isNaN(lng)) return null;

          return (
            <Marker
              key={id}
              position={{ lat, lng }}
              icon={{
                url: hoveredId === id ? "/images/marker.svg" : "/images/markerH.svg",
                scaledSize: new window.google.maps.Size(36, 36),
                anchor: new window.google.maps.Point(18, 36),
              }}
              onMouseOver={() => setHoveredId(id)}
              onMouseOut={() => setHoveredId(null)}
              onClick={() => onMarkerClick(id || item)}
              // zIndex={hoveredId === id ? 1000 : 1}
            />
          );
        })}
      </GoogleMap>
    </div>
  );
};

export default React.memo(MapWithResults);